/**
 * MessageBubble Component
 * 
 * Renders a single message from user or AI
 */

import { getLocalizedCharacterName, MultilingualCharacterName } from '../utils/characterName';
import { logger } from '../utils/logger';

interface MessageBubbleProps {
  speaker: 'user' | 'ai';
  text: string;
  avatarImageUrl?: string;
  characterName?: string | MultilingualCharacterName;
  audioBlob?: Blob; // 🔊 Recorded audio for debugging
  onPauseMicrophone?: () => void;
  onResumeMicrophone?: () => void;
}

export default function MessageBubble({ 
  speaker, 
  text, 
  avatarImageUrl, 
  characterName, 
  audioBlob,
  onPauseMicrophone,
  onResumeMicrophone 
}: MessageBubbleProps) {
  const playAudio = () => {
    if (!audioBlob) return;
    
    // Pause microphone to prevent avatar from responding to playback
    logger.debug('[🔊 MessageBubble] Pausing microphone before playback');
    onPauseMicrophone?.();
    
    const url = URL.createObjectURL(audioBlob);
    const audio = new Audio(url);
    
    audio.onended = () => {
      URL.revokeObjectURL(url);
      // Resume microphone after playback ends
      logger.debug('[🔊 MessageBubble] Resuming microphone after playback');
      onResumeMicrophone?.();
    };
    
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      // Resume microphone even on error
      console.error('[🔊 MessageBubble] Playback error - resuming microphone');
      onResumeMicrophone?.();
    };
    
    audio.play().catch(err => {
      console.error('[🔊 MessageBubble] Failed to play audio:', err);
      URL.revokeObjectURL(url);
      // Resume microphone if play fails
      onResumeMicrophone?.();
    });
  };
  
  if (speaker === 'user') {
    return (
      <div className="flex items-start gap-2 md:gap-3 justify-end pr-3 md:pr-8 my-0">
        <div className="p-2 md:p-3 rounded-lg bg-green-600/40 backdrop-blur-sm border border-green-400/20 max-w-[75%] md:max-w-md">
          <div className="flex items-center gap-2">
            <span className="text-white text-xs md:text-sm flex-1 leading-relaxed">{text}</span>
            {audioBlob && (
              <button
                onClick={playAudio}
                className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-green-500/50 hover:bg-green-500 transition-colors"
                title="Play recorded audio"
              >
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                </svg>
              </button>
            )}
          </div>
        </div>
        <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-green-500 flex-shrink-0"></div>
      </div>
    );
  }

  // AI message
  const localizedName = getLocalizedCharacterName(characterName);
  
  return (
    <div className="flex items-start gap-2 md:gap-3 justify-start pl-3 md:pl-8 my-0">
      {avatarImageUrl && (
        <div className="w-6 h-6 md:w-8 md:h-8 rounded-full overflow-hidden flex-shrink-0">
          <img
            src={avatarImageUrl}
            alt={localizedName}
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 15%', transform: 'scale(1.3)' }}
          />
        </div>
      )}
      <div className="p-2 md:p-3 rounded-lg bg-gray-800/50 backdrop-blur-sm border border-gray-600/20 max-w-[75%] md:max-w-md">
        <span className="text-white text-xs md:text-sm leading-relaxed">{text}</span>
      </div>
    </div>
  );
}

